import {
  compose,
  isEmpty,
  pathOr,
  reduce,
  toPairs,
} from 'ramda';
import { useMemo } from 'react';
import { shallowEqual, useSelector, useStore } from 'react-redux';

import { type Locale } from '../store/reducers/locale';
import { localeStatusSelector } from '../store/selectors';
import { RootState } from '../store/types';

const getAllLocalesSelector = (state: RootState) => state.locale?.locales ?? {};

type Locales<T> = Record<keyof T, string>;
type VariableValue = Function | string | number;
type Variables = Record<string, VariableValue>;

export const replaceVariables = <T extends Locale>(
  variables: Variables,
  locales: Locales<T>
) => (str: string) => {
    if (isEmpty(variables)) return str;

    let newString = str;
    for (const [ key, value ] of toPairs(variables)) {
      const re = new RegExp(`{{${key}}}`, 'g');
      newString = newString.replace(
        re,
        typeof value === 'function' ? value(locales) : value
      );
    }

    return newString;
  };

type UseIntlArgs<T> = {
  keys: T;
  variables?: Variables;
  fallback?: Locales<T>;
};

export const useIntl = <T extends Locale>(
  { keys,
    variables = {},
    fallback }: UseIntlArgs<T>) => {
  const {
    disabled,
    loaded,
    lang,
  } = useSelector(localeStatusSelector, shallowEqual);

  const store = useStore<RootState>();

  const keysCheck = Object.values(keys).join(',');

  const variablesCheck = Object.values(variables).join(',');

  const extracted: Locales<T> = useMemo(() => {
    const locales = getAllLocalesSelector(store.getState()) as Locales<T>;
    if (disabled) {
      return keys;
    }

    return compose(
      reduce(
        (acc, [ k, v ]: [string, string]) => ({
          ...acc,
          ...{
            [k]: compose(
              replaceVariables<T>(variables, locales),
              pathOr(fallback?.[k] ?? v, v.split('.')),
            )(locales),
          },
        }),
        {} as Locales<T>
      ),
      toPairs
    )(keys);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ keysCheck, variablesCheck, disabled, lang, loaded ]);

  return extracted;
};
